"use client";

import { Button } from "@templates/design-system";
import { cn } from "@newxsystems/shared";
import { useEffect, useRef, useState } from "react";
import { BlockImage } from "../BlockImage/BlockImage";
import type { IBlockVideoPlayerProps } from "./BlockVideoPlayer.props";

const autoplayQuery = /(^|&)autoplay=[^&]*/u;

const withAutoplay = (url: string) => {
  const hashIndex = url.indexOf("#");
  const hash = hashIndex === -1 ? "" : url.slice(hashIndex);
  const destination = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const queryIndex = destination.indexOf("?");

  if (queryIndex === -1) {
    return `${destination}?autoplay=1${hash}`;
  }

  const query = destination.slice(queryIndex + 1);
  const nextQuery = autoplayQuery.test(query)
    ? query.replace(autoplayQuery, "$1autoplay=1")
    : `${query}&autoplay=1`;

  return `${destination.slice(0, queryIndex + 1)}${nextQuery}${hash}`;
};

export const BlockVideoPlayer = ({
  banner,
  className,
  playerClassName,
  source,
  title,
  url,
}: IBlockVideoPlayerProps) => {
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const showBanner = Boolean(banner) && !bannerDismissed;
  const playerUrl = source === "direct" || !bannerDismissed ? url : withAutoplay(url);

  useEffect(() => {
    if (source !== "direct" && bannerDismissed) {
      iframeRef.current?.focus();
    }
  }, [bannerDismissed, source]);

  const handlePlay = () => {
    setBannerDismissed(true);

    if (source === "direct") {
      const video = videoRef.current;

      if (video) {
        video.focus();
        void video.play().catch(() => undefined);
      }
    }
  };

  return (
    <div
      className={cn(
        "relative isolate aspect-video w-full overflow-hidden bg-foreground",
        className,
      )}
    >
      {source === "direct" ? (
        <video
          aria-hidden={showBanner || undefined}
          aria-label={title}
          className={cn("absolute inset-0 h-full w-full", playerClassName)}
          controls
          ref={videoRef}
          src={url}
          tabIndex={showBanner ? -1 : 0}
          title={title}
        />
      ) : (
        <iframe
          allow="autoplay; fullscreen; picture-in-picture"
          aria-hidden={showBanner || undefined}
          className={cn("absolute inset-0 h-full w-full border-0", playerClassName)}
          ref={iframeRef}
          src={playerUrl}
          tabIndex={showBanner ? -1 : 0}
          title={title}
          allowFullScreen
        />
      )}

      {banner && !bannerDismissed ? (
        <div className="absolute inset-0 z-10 grid place-items-center">
          <BlockImage
            aspectRatioClassName="h-full w-full"
            effectiveAspectRatio="16:9"
            image={banner}
            className="absolute inset-0 h-full w-full"
            imageClassName="object-cover"
            loading="eager"
          />
          <div aria-hidden="true" className="absolute inset-0 bg-foreground/30" />
          <Button
            aria-label="Play video"
            className="relative z-10 rounded-full border border-background px-5 font-mono text-[12px]/[1.45] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-electric-blue focus-visible:ring-offset-2 focus-visible:ring-offset-foreground motion-reduce:transition-none"
            onClick={handlePlay}
            type="button"
            variant="primary"
          >
            Play video
          </Button>
        </div>
      ) : null}

      {bannerDismissed && banner ? (
        <span aria-live="polite" className="sr-only">
          Video player active
        </span>
      ) : null}
    </div>
  );
};

BlockVideoPlayer.displayName = "Shared::BlockVideoPlayer";
